import React, { useState } from 'react';
import { IOrder } from '../../interface/IOrder.ts';
import { useHelper } from '../../features/functions/helper.tsx';
import Button from '../Form/Button.tsx';
import ToggleIcon from '../../img/inline/ToggleIcon.tsx';

const EXAM_TYPE_LABELS:{ [key:string]:string } = {
    exam_type_efa_cfp: 'Anmeldung EFA® / CFP®-Zentralprüfung',
    exam_type_din_competence: 'Anmeldung DIN-Kompetenzprüfung',
    exam_type_cga_cfep: 'Anmeldung CGA® / CFEP®-Zentralprüfung',
};

const PARTICIPATION_LABELS:{ [key:string]:string } = {
    participation_first_attempt: 'erstmalige Teilnahme',
    participation_repeat: '1. Wiederholung',
    participation_second_repeat: '2. Wiederholung',
};

const CERTIFICATE_PROOF_LABELS:{ [key:string]:string } = {
    certificate_already_on_file: 'liegt beim FPSB vor',
    certificate_attached: 'liegt beim FPSB vor',
    certificate_to_be_submitted: 'wird bis spätestens zum Anmeldeschluss nachgereicht',
};

// Whether an invoice was already created is nothing the advisor needs on the badge.
const STATUS_LABELS:{ [key:string]:string } = {
    invoice_paid: 'Rechnung bezahlt',
    cancelled_with_invoice: 'Storniert',
};

// Maps each status to a semantic colour group for the status badge.
const STATUS_VARIANTS:{ [key:string]:string } = {
    received: 'info',
    invoiced: 'info',
    invoice_paid: 'success',
    approved: 'success',
    result_available: 'success',
    withdraw_before_exam: 'warning',
    excused_absence: 'warning',
    cancelled_with_invoice: 'danger',
    failed_attempt: 'danger',
};

// Orders that are still in progress; afterwards a late certificate is of no use.
// invoice_paid is still the "invoiced" workflow phase, only with the invoice paid.
const CERTIFICATE_UPLOAD_STATUSES = ['received', 'invoiced', 'invoice_paid', 'approved'];

const isSet = (value:any) => value === '1' || value === 1 || value === true;

const OrderItem = ({
    order,
    isCancelling,
    onCancel,
    onRetry,
    onEarlyCheck,
    onUploadCertificate,
    initialExpanded,
    educationPrograms,
}:{
    order:IOrder,
    isCancelling:boolean,
    onCancel:(order:IOrder) => void,
    onRetry:(order:IOrder) => void,
    onEarlyCheck:(order:IOrder) => void,
    onUploadCertificate:(order:IOrder) => void,
    initialExpanded?:boolean,
    // "Aus- und Weiterbildungsprogramme" entries, to resolve order.education_program.
    educationPrograms:Array<any>,
}) => {
    const { formatDateFromTimestamp } = useHelper();
    const [expanded, setExpanded] = useState(!!initialExpanded);

    // order_date / created are Unix timestamps …
    const formatDate = (value:any) => (value && Number(value) > 0 ? formatDateFromTimestamp(value, 'dd.mm.yyyy') : '—');
    // … whereas the form-submitted dates come back as "ddmmyyyy" strings.
    const formatFormDate = (value:any) => (typeof value === 'string' && /^\d{8}$/.test(value)
        ? `${value.slice(0, 2)}.${value.slice(2, 4)}.${value.slice(4, 8)}`
        : '—');

    const educationLabel = order.education_program != null
        ? (educationPrograms.find(entry => String(entry.id) === String(order.education_program))?.label ?? '')
        : '';

    const selectedExamTypes = Object.keys(EXAM_TYPE_LABELS).filter(key => isSet((order as any)[key]));
    const orderTitle = selectedExamTypes.length > 0
        ? selectedExamTypes.map(key => EXAM_TYPE_LABELS[key]).join(', ')
        : (order.subject || '—');

    // The 2nd repeat wins over the 1st: a backend without the separate checkbox
    // reports every repeat as participation_repeat.
    const isRepeat = isSet(order.participation_repeat) || isSet(order.participation_second_repeat);
    let participationLabel = '—';
    if (isSet(order.participation_second_repeat)) {
        participationLabel = PARTICIPATION_LABELS.participation_second_repeat;
    } else if (isSet(order.participation_repeat)) {
        participationLabel = PARTICIPATION_LABELS.participation_repeat;
    } else if (isSet(order.participation_first_attempt)) {
        participationLabel = PARTICIPATION_LABELS.participation_first_attempt;
    }

    // Also after invoicing — the cancellation fees then apply.
    const canCancel = ['received', 'invoiced', 'invoice_paid', 'approved'].includes(order.status_value as string);
    const canRetry = order.status_value === 'failed_attempt';
    const canRequestEarlyCheck = order.status_value === 'received' && !isSet(order.request_early_check);
    const certificateUploaded = isSet(order.educational_certificate);
    const certificateMissing = order.certificate_proof === 'certificate_to_be_submitted'
        && !certificateUploaded
        && CERTIFICATE_UPLOAD_STATUSES.includes(order.status_value as string);
    // A certificate handed in later reads like one uploaded with the registration.
    const certificateProofLabel = order.certificate_proof === 'certificate_to_be_submitted' && certificateUploaded
        ? CERTIFICATE_PROOF_LABELS.certificate_attached
        : (order.certificate_proof ? (CERTIFICATE_PROOF_LABELS[order.certificate_proof] || order.certificate_proof) : '—');
    const statusVariant = STATUS_VARIANTS[order.status_value as string] || 'neutral';
    const statusLabel = STATUS_LABELS[order.status_value as string] || order.status;

    return (
        <div id={`order-${order.id}`} className="single-repeater-item order-item">
            <div className="repeater-toggle-headline" onClick={() => setExpanded(!expanded)}>
                <div className="order-head">
                    <span className="order-subject">{orderTitle}</span>
                    <span className="order-date">{formatDate(order.order_date)}</span>
                    <span className={`order-status status-${statusVariant}`}>{statusLabel}</span>
                    { certificateMissing && (
                        <span className="order-status status-warning">Nachweis fehlt</span>
                    ) }
                    {/* Expanded, the detail view carries the upload button instead. */}
                    { certificateMissing && !expanded && (
                        <div className="order-actions" onClick={(e) => e.stopPropagation()}>
                            <Button
                                type="button"
                                buttonType="primary"
                                className="compact"
                                onClick={() => onUploadCertificate(order)}
                            >Dokument hochladen</Button>
                        </div>
                    ) }
                </div>
                <ToggleIcon toggled={expanded} width={15} height={15} />
            </div>

            { expanded && (
                <div className="order-detail">
                    <dl className="order-detail-list">
                        <div className="order-detail-row">
                            <dt>Prüfungsnummer</dt>
                            <dd>{order.exam_number ? <strong>{order.exam_number}</strong> : '—'}</dd>
                        </div>
                        <div className="order-detail-row">
                            <dt>Teilnahme</dt>
                            <dd>{participationLabel}</dd>
                        </div>
                        { isRepeat && (
                            <div className="order-detail-row">
                                <dt>Erstmalige Teilnahme am</dt>
                                <dd>{formatFormDate(order.first_attempt_date)}</dd>
                            </div>
                        ) }
                        { educationLabel && (
                            <div className="order-detail-row">
                                <dt>Aus-/Weiterbildung</dt>
                                <dd>{educationLabel}</dd>
                            </div>
                        ) }
                        <div className="order-detail-row">
                            <dt>Nachweis Abschlusszeugnis</dt>
                            <dd>{certificateProofLabel}</dd>
                        </div>
                        <div className="order-detail-row">
                            <dt>AGB akzeptiert</dt>
                            <dd>{isSet(order.accept_agb) ? 'Ja' : 'Nein'}</dd>
                        </div>
                        <div className="order-detail-row">
                            <dt>Datenschutz zur Kenntnis genommen</dt>
                            <dd>{isSet(order.accept_privacy) ? 'Ja' : 'Nein'}</dd>
                        </div>
                        <div className="order-detail-row">
                            <dt>Widerrufsbelehrung zur Kenntnis genommen</dt>
                            <dd>{isSet(order.accept_withdrawal) ? 'Ja' : 'Nein'}</dd>
                        </div>
                        <div className="order-detail-row">
                            <dt>Vorzeitige Prüfung der Teilnahmevoraussetzungen verlangt</dt>
                            <dd>{isSet(order.request_early_check) ? 'Ja' : 'Nein'}</dd>
                        </div>
                        <div className="order-detail-row">
                            <dt>Prüfungsordnung anerkannt</dt>
                            <dd>{isSet(order.accept_exam_regulations) ? 'Ja' : 'Nein'}</dd>
                        </div>
                        { order.created && (
                            <div className="order-detail-row">
                                <dt>Erstellt am</dt>
                                <dd>{formatDate(order.created)}</dd>
                            </div>
                        ) }
                    </dl>

                    { (canCancel || canRetry || canRequestEarlyCheck || certificateMissing) && (
                        <div className="order-actions">
                            { certificateMissing && (
                                <Button
                                    type="button"
                                    buttonType="primary"
                                    className="compact"
                                    onClick={() => onUploadCertificate(order)}
                                >Dokument hochladen</Button>
                            ) }
                            { canRequestEarlyCheck && (
                                <Button
                                    type="button"
                                    buttonType="primary"
                                    className="compact"
                                    icon="check"
                                    onClick={() => onEarlyCheck(order)}
                                >Verzicht auf Widerspruchsfrist & sofortige Bearbeitung anfordern</Button>
                            ) }
                            { canCancel && (
                                <Button
                                    type="button"
                                    buttonType="filled-danger"
                                    className="compact"
                                    icon="x"
                                    disabled={isCancelling}
                                    onClick={() => onCancel(order)}
                                >Auftrag stornieren</Button>
                            ) }
                            { canRetry && (
                                <Button
                                    type="button"
                                    buttonType="primary"
                                    className="compact"
                                    icon="arrow"
                                    onClick={() => onRetry(order)}
                                >Erneut anmelden</Button>
                            ) }
                        </div>
                    ) }
                </div>
            ) }
        </div>
    );
};

export default OrderItem;
